import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useApi } from '@fortem-monorepo/react-spa'
import type { AccruedInterests } from '@fortem-monorepo/web-sdk'

import { toastApiError } from '@/lib/error-toast'
import { accruedInterestKeys } from '@/react-query/queries/accrued-interests'

export const chartEmailMutationKey = ['operations', 'accrued-interests', 'email-chart'] as const

export const useEmailAccruedBalanceChart = () => {
  const api = useApi()
  return useMutation({
    mutationKey: chartEmailMutationKey,
    retry: false,
    mutationFn: (params: AccruedInterests.EmailChartParams) => api.accruedInterests.emailBalanceChart(params),
    onSuccess: (result) => toast.success(`Chart CSV email sent to ${result.recipient}`),
    onError: (error) => toastApiError(error, 'Unable to email chart datapoints'),
  })
}

export const useAdjustDailyAccruedInterest = () => {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: (params: AccruedInterests.AdjustDailyParams) => api.accruedInterests.adjustDaily(params),
    onSuccess: () => toast.success('Daily accrued interest updated'),
    onError: (error) => toastApiError(error, 'Unable to update daily accrued interest'),
    onSettled: () => client.invalidateQueries({ queryKey: accruedInterestKeys.all }),
  })
}

export const useConfirmAccruedInterestBalances = () => {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: (params: AccruedInterests.ConfirmBalancesParams) => api.accruedInterests.confirmBalances(params),
    onSuccess: (rows) => toast.success(`${rows.length} accrued interest balances confirmed`),
    onError: (error) => toastApiError(error, 'Unable to confirm accrued interest balances'),
    // Logging or response validation can fail after commit, so refresh on either outcome.
    onSettled: () => client.invalidateQueries({ queryKey: accruedInterestKeys.all }),
  })
}
